import type { MemberListItem } from "@albertine/types";
import { isBefore, subDays } from "date-fns";
import { z } from "zod";
import type { Conversation } from "../../../common/types/Conversation";
import type { Dictionary } from "../../../common/types/Dictionary";
import type { FirebaseTimestamp } from "../../../common/types/FirebaseTimestamp";
import { takeIfStringMatches } from "../../../utils/filter.util";
import { fromTimestampToDate } from "../../../utils/timestamp.util";

export const AgentEngagementLevel = z.enum([
    "messageSentWithinWeek",
    "messageSentWithinTwoWeek",
    "messageSentOverTwoWeek",
]);
export type AgentEngagementLevel = z.infer<typeof AgentEngagementLevel>;
export const AgentEngagementLevelList = AgentEngagementLevel.options;

export const AgentEngagementLevelMap: Record<AgentEngagementLevel, string> = {
    messageSentWithinWeek: "Within a week",
    messageSentWithinTwoWeek: "1-2 weeks ago",
    messageSentOverTwoWeek: "Over 2 weeks ago",
};

export type FilterByAgentEngagementLevel = {
    id: AgentEngagementLevel;
    name: string;
    count: number;
};

export function calculateEngagement(
    now: Date,
    lastMessageAt: FirebaseTimestamp | null | undefined,
): AgentEngagementLevel | undefined {
    if (!lastMessageAt) {
        return "messageSentOverTwoWeek";
    }

    const messageAt = fromTimestampToDate(lastMessageAt);

    if (isBefore(messageAt, subDays(now, 14))) {
        return "messageSentOverTwoWeek";
    }

    if (isBefore(messageAt, subDays(now, 7))) {
        return "messageSentWithinTwoWeek";
    }

    return "messageSentWithinWeek";
}

export function getAgentEngagementCounts(
    members: MemberListItem[],
    conversations: Dictionary<Conversation>,
): FilterByAgentEngagementLevel[] {
    const possibleEngagementLevels: FilterByAgentEngagementLevel[] =
        AgentEngagementLevelList.map((level) => ({
            id: level,
            name: AgentEngagementLevelMap[level],
            count: 0,
        }));
    const now = new Date();

    members.forEach((member) => {
        const engagementLevel = calculateEngagement(
            now,
            conversations[member.conversationId]
                ? conversations[member.conversationId].lastMessageByAgentAt
                : null,
        );

        const matchIndex = possibleEngagementLevels.findIndex(
            (it) => it.id === engagementLevel,
        );

        if (matchIndex >= 0) {
            const { count, ...rest } = possibleEngagementLevels[matchIndex];
            possibleEngagementLevels[matchIndex] = {
                ...rest,
                count: count + 1,
            };
        }
    });

    return possibleEngagementLevels;
}

export function filterMembersByAgentEngagement(
    engagementLevels: AgentEngagementLevel[],
    members: MemberListItem[],
    conversations: Dictionary<Conversation>,
) {
    const now = new Date();
    return takeIfStringMatches<MemberListItem>(
        members,
        (member) => {
            const lastMessageAt = conversations[member.conversationId]
                ? conversations[member.conversationId].lastMessageByAgentAt
                : null;
            return calculateEngagement(now, lastMessageAt);
        },
        engagementLevels,
    );
}
